import React from 'react'
import type { DiffSegment } from '../../../../../types/script-refinement'
import clsx from 'clsx'

export const DiffPreview: React.FC<{ segments?: DiffSegment[]; className?: string }> = ({ segments, className }) => {
  if (!segments || segments.length === 0) return null

  // Reconstruct full old/new strings from character-level segments
  const oldText = segments.map(s => (s.type === 'insert' ? '' : s.value || '')).join('')
  const newText = segments.map(s => (s.type === 'delete' ? '' : s.value || '')).join('')

  type Tok = { t: string; isSpace: boolean }
  const tokenize = (s: string): Tok[] => {
    if (!s) return []
    const parts = s.split(/(\s+)/)
    return parts.filter(p => p.length > 0).map(p => ({ t: p, isSpace: /\s+/.test(p) }))
  }

  const oldTokens = tokenize(oldText)
  const newTokens = tokenize(newText)
  const oldWords = oldTokens.filter(t => !t.isSpace).map(t => t.t)
  const newWords = newTokens.filter(t => !t.isSpace).map(t => t.t)

  // LCS on word tokens for a cleaner, word-level diff
  const m = oldWords.length
  const n = newWords.length
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0))
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      dp[i][j] = oldWords[i] === newWords[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1])
    }
  }

  type Op = { type: 'equal' | 'insert' | 'delete'; text: string }
  const ops: Op[] = []
  let i = 0, j = 0
  while (i < m || j < n) {
    if (i < m && j < n && oldWords[i] === newWords[j]) {
      ops.push({ type: 'equal', text: newWords[j] })
      i++; j++
    } else if (j < n && (i === m || dp[i][j + 1] >= dp[i + 1][j])) {
      ops.push({ type: 'insert', text: newWords[j] })
      j++
    } else if (i < m) {
      ops.push({ type: 'delete', text: oldWords[i] })
      i++
    }
  }

  // Helper to emit any whitespace from newTokens between word indices
  const newWordIdx: number[] = []
  newTokens.forEach((tok, idx) => { if (!tok.isSpace) newWordIdx.push(idx) })
  const spacesBetween = (fromWordIdxExclusive: number) => {
    // fromWordIdxExclusive: index in newWordIdx of last emitted word; start at -1
    const fromTok = fromWordIdxExclusive < 0 ? 0 : (newWordIdx[fromWordIdxExclusive] + 1)
    const toTok = nextNewWordPos < newWordIdx.length ? newWordIdx[nextNewWordPos] : newTokens.length
    const slice = newTokens.slice(fromTok, toTok).map(t => (t.isSpace ? t.t : ''))
    return slice.join('')
  }

  let emittedNewWords = -1
  let nextNewWordPos = 0

  const parts: React.ReactNode[] = []
  ops.forEach((op, idx) => {
    if (op.type === 'insert' || op.type === 'equal') {
      // Append spaces present before this word in new text
      const ws = spacesBetween(emittedNewWords)
      if (ws) parts.push(<span key={`ws-${idx}`} className="whitespace-pre-wrap">{ws}</span>)
      // Emit the word
      if (op.type === 'insert') {
        parts.push(
          <span key={`ins-${idx}`} className="inline-block rounded-md bg-success-50 px-1 text-success-700 ring-1 ring-success-100">{op.text}</span>
        )
      } else {
        parts.push(<span key={`eq-${idx}`} className="text-foreground/70">{op.text}</span>)
      }
      emittedNewWords = nextNewWordPos
      nextNewWordPos++
    } else {
      // delete stays inline without consuming new-word spacing
      parts.push(
        <span key={`del-${idx}`} className="inline-block rounded-md bg-danger-50 px-1 text-danger-600 ring-1 ring-danger-100 line-through">{op.text}</span>
      )
    }
  })

  // Trailing spaces after the last emitted new word
  const trailingFromTok = emittedNewWords < 0 ? 0 : (newWordIdx[emittedNewWords] + 1)
  const trailingWs = newTokens.slice(trailingFromTok).map(t => (t.isSpace ? t.t : '')).join('')
  if (trailingWs) parts.push(<span key="ws-end" className="whitespace-pre-wrap">{trailingWs}</span>)

  return (
    <span className={clsx('text-sm leading-relaxed text-foreground/75 whitespace-pre-wrap break-words', className)}>
      {parts}
    </span>
  )
}
